import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  ClaimContext,
  DocumentsContext,
  ReportContext,
  FormFile,
  OverlayFile,
  PacksFile,
  PatientPanel
} from '../models/sut-models';

import { OverlayService } from '../services/overlay.service';
import { RuleEngineService } from '../services/rule-engine.service';

// ---- UI helper types (frontend-only) ----
interface DrugEntry { key: string; label: string; }
interface QuickPreset {
  id: string;
  label: string;
  // Optional: direct indication jump
  indicationCode?: string;
  // Partial state patches
  model?: Record<string, any>;
  claim?: Partial<ClaimContext>;
  documents?: Partial<DocumentsContext>;
  report?: Partial<ReportContext>;
}
interface IcdPreset { code: string; label: string; when?: string | null; }

@Component({ selector: 'app-form-runner', templateUrl: './form-runner.component.html' })
export class FormRunnerComponent implements OnInit, OnDestroy {
  packs!: PacksFile;
  overlay!: OverlayFile;
  form!: FormFile;

  drugs: DrugEntry[] = [];
  drugKey = '';

  indicationCode = '';
  model: Record<string, any> = {};
  outcomeText = '';
  reasons: string[] = [];
  hints: string[] = [];

  Claim: ClaimContext = {
    Setting: 'daycare',
    IsFirstPrescription: true,
    SupplyDays: 21,
    SutExclusionArticle: null,
    ColoredRx: false,
    OffLabelApproval: false,
    ImportedDrug: false,
    SgkSpecificExcluded: false,
    ICD10: ''
  };
  Documents: DocumentsContext = { has_health_report: true as any } as any; // tolerate different naming
  Report: ReportContext = { IssuerSpecialty: 'Tıbbi Onkoloji', ValidityYears: 1 };

  quickPresets: QuickPreset[] = [];
  icdPresets: IcdPreset[] = [];
  selectedIcd = '';

  patientPanel: PatientPanel | null = null;

  private subs = new Subscription();

  constructor(
    private http: HttpClient,
    private overlays: OverlayService,
    private engine: RuleEngineService
  ) {}

  // ---------- Lifecycle ----------
  ngOnInit(): void {
    const sp = this.overlays.loadPacks().subscribe(p => {
      this.packs = p;
      this.loadManifest(() => this.autoPickFirstDrug());
    });
    this.subs.add(sp);
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  // ---------- Manifest & Loading ----------
  private loadManifest(after?: () => void) {
    const s = this.http.get<DrugEntry[]>('assets/sut/manifest.json')
      .pipe(catchError(() => of<DrugEntry[]>([])))
      .subscribe(list => {
        this.drugs = Array.isArray(list) ? list : [];
        if (after) after();
      });
    this.subs.add(s);
  }

  private autoPickFirstDrug() {
    if (this.drugs.length) {
      this.drugKey = this.drugs[0].key;
    } else {
      this.drugKey = 'pembrolizumab'; // fallback
    }
    this.loadDrug(this.drugKey);
  }

  onDrugChange() {
    if (!this.drugKey) return;
    this.loadDrug(this.drugKey);
  }

  loadDrug(drug: string) {
    // reset dynamic bits
    this.quickPresets = [];
    this.icdPresets = [];
    this.selectedIcd = '';
    this.patientPanel = null;

    const s = this.overlays.loadOverlay(drug).subscribe(overlay => {
      this.overlay = overlay;

      // pick first indication & form
      const first = overlay.indications?.[0];
      this.indicationCode = first?.code ?? '';
      const formId = first?.form_id ?? '';

      // defaults
      this.model = this.createDefaultModel(overlay?.meta?.name || drug);
      this.Claim = {
        Setting: 'daycare',
        IsFirstPrescription: true,
        SupplyDays: 21,
        SutExclusionArticle: null,
        ColoredRx: false,
        OffLabelApproval: false,
        ImportedDrug: false,
        SgkSpecificExcluded: false,
        ICD10: this.initialIcdByOverlay(overlay)
      };
      this.Documents = { has_health_report: true } as any;
      this.Report = { IssuerSpecialty: this.defaultIssuerByOverlay(overlay), ValidityYears: 1 };

      // load form + dynamic assets
      if (formId) {
        this.loadForm(formId);
        this.loadPatientPanel(drug, formId);
      }
      this.loadIcdPresets(drug);
    });
    this.subs.add(s);
  }

  private loadForm(formId: string) {
    const s = this.overlays.loadForm(formId).subscribe(f => {
      this.form = f;
      this.loadQuickPresets(formId);
      this.evaluate();
    });
    this.subs.add(s);
  }

  onIndicationChange(code: string) {
    this.indicationCode = code;
    const found = this.overlay?.indications?.find(i => i.code === code);
    if (found) {
      this.loadForm(found.form_id);
      this.loadPatientPanel(this.drugKey, found.form_id);
    } else {
      this.evaluate();
    }
  }

  // ---------- Optional external data files ----------
  private loadQuickPresets(formId: string) {
    const url = `assets/sut/forms/${formId}.presets.json`;
    const s = this.http.get<QuickPreset[]>(url)
      .pipe(catchError(() => of<QuickPreset[]>([])))
      .subscribe(list => this.quickPresets = Array.isArray(list) ? list : []);
    this.subs.add(s);
  }

  private loadIcdPresets(drugKey: string) {
    const url = `assets/sut/icd/${drugKey}.icd.json`;
    const s = this.http.get<IcdPreset[]>(url)
      .pipe(catchError(() => of<IcdPreset[]>([])))
      .subscribe(list => this.icdPresets = Array.isArray(list) ? list : []);
    this.subs.add(s);
  }

  private loadPatientPanel(drugKey: string, formId: string) {
    // try form-specific → then drug-specific → else null
    const byForm = `assets/sut/panels/${formId}.panel.json`;
    const byDrug = `assets/sut/panels/${drugKey}.panel.json`;
    const s = this.http.get<PatientPanel>(byForm).pipe(
      catchError(() => this.http.get<PatientPanel>(byDrug).pipe(catchError(() => of(null))))
    ).subscribe(p => this.patientPanel = p);
    this.subs.add(s);
  }

  // ---------- Apply presets ----------
  applyPreset(p: QuickPreset) {
    // indication jump (optional)
    if (p.indicationCode && p.indicationCode !== this.indicationCode) {
      this.onIndicationChange(p.indicationCode);
    }
    // merge shallow
    if (p.model) Object.assign(this.model, p.model);
    if (p.claim) Object.assign(this.Claim, p.claim);
    if (p.documents) Object.assign(this.Documents, p.documents);
    if (p.report) Object.assign(this.Report, p.report);

    this.selectedIcd = this.Claim.ICD10 || '';
    this.evaluate();
  }

  applyIcd(code: string) {
    this.Claim.ICD10 = code;
    this.evaluate();
  }

  resetModel() {
    this.model = this.createDefaultModel(this.overlay?.meta?.name || this.drugKey);
    this.evaluate();
  }

  // ---------- Evaluation ----------
  evaluate(): void {
    if (!this.packs || !this.overlay || !this.form) return;

    const scope = {
      model: this.model,
      Claim: this.Claim,
      Documents: this.Documents,
      Report: this.Report,
      Params: this.overlay.params,
      Lists: this.overlay.lists
    };

    const packRes = this.engine.evaluatePacks(this.overlay, this.packs, scope);
    if (!packRes.ok) {
      this.outcomeText = 'NOT_COVERED';
      this.reasons = [`Genel SUT paketi koşulu sağlanmadı: ${packRes.failedPack}`];
      this.updateHints();
      return;
    }

    const res = this.engine.evaluateForm(this.form, scope);
    this.outcomeText = res.outcome;
    this.reasons = res.reasons || [];
    this.updateHints();
  }

  // ---------- Visibility helpers ----------
  visibleExpr(expr?: string | null): boolean {
    if (!expr) return true;
    try {
      // eslint-disable-next-line no-new-func
      const f = new Function(
        'model','Claim','Documents','Report','Params','Lists',
        '"use strict";return !!(' + expr + ')'
      );
      return !!f(this.model, this.Claim, this.Documents, this.Report, this.overlay?.params, this.overlay?.lists);
    } catch {
      return true; // fail-open
    }
  }
  visibleStep(step: any): boolean { return this.visibleExpr(step?.visibleIf ?? null); }
  visibleField(f: any): boolean { return this.visibleExpr(f?.visibleIf ?? null); }

  // ---------- Hints (optional) ----------
   updateHints() {
    const hs: string[] = [];
    const name = (this.overlay?.meta?.name || '').toLowerCase();

    // Example: Abatasept board composition hints
    if (name.includes('abatasept')) {
      const isAdult = (this.model.age ?? 18) >= 18;
      if (isAdult) {
        if ((this.model.prescriberSpecialty === 'Klinik İmmünoloji' || this.model.prescriberSpecialty === 'Fiziksel Tıp ve Rehabilitasyon')
            && +this.model.facilityLevel !== 3) {
          hs.push('Erişkin RA: Klinik İmmünoloji/FTR reçetesi için 3. basamak kurum şartı.');
        }
      } else {
        if (!this.model.boardHasPediatricRheumatology) {
          hs.push('Pediatrik JIA: Kurulda en az bir Çocuk Romatolojisi uzmanı olmalı.');
        }
      }
    }

    this.hints = hs;
  }

  // ---------- Defaults & helpers ----------
   createDefaultModel(hintName: string) {
    return {
      ECOG: 0, KPS: 70,
      PD_L1: 0, CPS: 0,
      adequateOrganFunction: true,
      activeBrainMets: false, symptomaticBrainMets: false,
      immunotherapyNaive: true, progressedOnImmunotherapy: false,

      // Common disease flags
      disease: '', stage: '', resectable: false, lineNumber: 1,

      // Histology/genomics
      histology: 'non_squamous',
      histologyNSCLC: 'squamous',
      EGFR_ALK_ROS_WT: true, EGFR_ALK_ROS_WildType: true,
      histologyMesothelioma: 'non_epithelioid',
      HER2_negative: false, MSI_H_or_dMMR: false,
      tissuePDL1NotFeasible: false,

      // Therapy planning
      regimen: '', ipiDosesPlanned: 0, nivolumabMonthsPlanned: 0,
      neoadjuvantCycles: 0, chemoCyclesWithCombo: 0,

      // Prior tx flags
      priorSystemicLines: 0, progressedAfterLastLine: false,
      priorVEGFTargetedCount: 0,
      platinumRelapseWithin6m: false, postCRTRecurrence: false,
      priorFPCombo: false, priorFPProgressed: false,
      priorFluoropyrimidineOxaliplatinIrinotecan: false,

      // Hematology
      autoHSCTDone: false, receivedBrentuximab: false,
      totalLinesIncludingHSCT: 0, includesAutoHSCTIn3Lines: false,
      isRenewal: false, responseAtRenewalAtLeastPR: false,

      // Abatasept-related
      age: 18, isContinuation: false,
      baselineDAS28: 0, currentDAS28At3mo: 0,
      priorDmardsAtLeast3InclMTX3mo: false, priorAntiTNFFailure: false,
      coTherapyMTX: false, reportMonths: 3, facilityLevel: 3,
      prescriberSpecialty: 'Tıbbi Onkoloji',
      boardHasPediatricRheumatology: false, boardHasImmunologyOrFTR: false,
      antiTNF3moTried: false, acrPed30ReachedDuringAntiTNF: false,
      acrPed_afterFirst3mo: 0, acrPed_current: 0
    } as Record<string, any>;
  }

   initialIcdByOverlay(overlay: OverlayFile): string {
    const name = overlay?.meta?.name?.toLowerCase?.() || '';
    if (name.includes('pembro')) return 'C34.9';
    if (name.includes('abatasept')) return 'M06.9';
    if (name.includes('nivo') || name.includes('ipilu') || name.includes('ipilimumab')) return 'C43.9';
    if (name.includes('durvalumab')) return 'C24.0';
    return '';
  }

   defaultIssuerByOverlay(overlay: OverlayFile): string {
    const name = overlay?.meta?.name?.toLowerCase?.() || '';
    if (name.includes('abatasept')) return 'Romatoloji';
    return 'Tıbbi Onkoloji';
  }
}